import { isConfigured, supabase } from './supabase';
import type { ReportRecord, ReportTemplate } from '../../shared/types/reports';

const LS_ARCHIVE_KEY = 'reports:archive:v1';
const LS_TEMPLATES_KEY = 'reports:templates:v1';

function readLS<T>(key: string, fallback: T): T {
  try { const s = localStorage.getItem(key); return s ? JSON.parse(s) as T : fallback; } catch { return fallback; }
}
function writeLS<T>(key: string, value: T) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

function genId(prefix: string) { return `${prefix}_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`; }

export const reportsService = {
  async saveReport(input: Omit<ReportRecord, 'id' | 'createdAt'>): Promise<{ id: string }> {
    const record: ReportRecord = { id: genId('rpt'), createdAt: new Date().toISOString(), ...input } as ReportRecord;
    if (!isConfigured) {
      const list = readLS<ReportRecord[]>(LS_ARCHIVE_KEY, []);
      list.unshift(record);
      writeLS(LS_ARCHIVE_KEY, list);
      return { id: record.id };
    }
    // DB path: best-effort; table may not exist yet
    const { data, error } = await supabase.from('reports').insert({
      id: record.id,
      client_id: record.clientId,
      campaign_ids: record.campaignIds,
      start_date: record.startDate,
      end_date: record.endDate,
      sources: record.include,
      html: record.html,
      metrics: record.metrics,
      author_user_id: record.authorUserId || null,
      created_at: record.createdAt,
    }).select('id').single();
    if (error) {
      // Fallback to local if table missing or RLS blocks
      const list = readLS<ReportRecord[]>(LS_ARCHIVE_KEY, []);
      list.unshift(record);
      writeLS(LS_ARCHIVE_KEY, list);
      return { id: record.id };
    }
    return { id: data.id };
  },

  async listReports(opts?: { clientId?: string; limit?: number }): Promise<ReportRecord[]> {
    if (!isConfigured) {
      let list = readLS<ReportRecord[]>(LS_ARCHIVE_KEY, []);
      if (opts?.clientId) list = list.filter(r => r.clientId === opts.clientId);
      if (opts?.limit) list = list.slice(0, opts.limit);
      return list;
    }
    const q = supabase.from('reports').select('*').order('created_at', { ascending: false });
    if (opts?.clientId) q.eq('client_id', opts.clientId);
    const { data, error } = await q;
    if (error || !Array.isArray(data)) return [];
    return data.map((r: any) => ({
      id: r.id,
      clientId: r.client_id,
      campaignIds: r.campaign_ids || [],
      startDate: r.start_date,
      endDate: r.end_date,
      include: r.sources || { meta: false, spotify: false, hypeedit: false },
      html: r.html,
      metrics: r.metrics,
      authorUserId: r.author_user_id,
      createdAt: r.created_at,
    })) as ReportRecord[];
  },

  async getReport(id: string): Promise<ReportRecord | null> {
    if (!isConfigured) {
      const list = readLS<ReportRecord[]>(LS_ARCHIVE_KEY, []);
      return list.find(r => r.id === id) || null;
    }
    const { data, error } = await supabase.from('reports').select('*').eq('id', id).single();
    if (error || !data) return null;
    const r = data as any;
    return {
      id: r.id,
      clientId: r.client_id,
      campaignIds: r.campaign_ids || [],
      startDate: r.start_date,
      endDate: r.end_date,
      include: r.sources || { meta: false, spotify: false, hypeedit: false },
      html: r.html,
      metrics: r.metrics,
      authorUserId: r.author_user_id,
      createdAt: r.created_at,
    } as ReportRecord;
  },

  async saveTemplate(t: Omit<ReportTemplate, 'id' | 'createdAt'>): Promise<{ id: string }> {
    const rec: ReportTemplate = { id: genId('tpl'), createdAt: new Date().toISOString(), ...t } as ReportTemplate;
    if (!isConfigured) {
      const list = readLS<ReportTemplate[]>(LS_TEMPLATES_KEY, []);
      list.unshift(rec);
      writeLS(LS_TEMPLATES_KEY, list);
      return { id: rec.id };
    }
    const { data, error } = await supabase.from('report_templates').insert({
      id: rec.id,
      name: rec.name,
      client_id: rec.clientId || null,
      campaign_ids: rec.campaignIds || [],
      default_sources: rec.defaultSources || null,
      default_timeframe: rec.defaultTimeframe || null,
      created_by: rec.createdBy || null,
      created_at: rec.createdAt,
    }).select('id').single();
    if (error) {
      const list = readLS<ReportTemplate[]>(LS_TEMPLATES_KEY, []);
      list.unshift(rec);
      writeLS(LS_TEMPLATES_KEY, list);
      return { id: rec.id };
    }
    return { id: data.id };
  },

  async listTemplates(opts?: { clientId?: string }): Promise<ReportTemplate[]> {
    if (!isConfigured) {
      let list = readLS<ReportTemplate[]>(LS_TEMPLATES_KEY, []);
      if (opts?.clientId) list = list.filter(t => t.clientId === opts.clientId);
      return list;
    }
    const q = supabase.from('report_templates').select('*').order('created_at', { ascending: false });
    if (opts?.clientId) q.eq('client_id', opts.clientId);
    const { data, error } = await q;
    if (error || !Array.isArray(data)) return [];
    return data.map((r: any) => ({
      id: r.id,
      name: r.name,
      clientId: r.client_id,
      campaignIds: r.campaign_ids || [],
      defaultSources: r.default_sources || {},
      defaultTimeframe: r.default_timeframe || {},
      createdBy: r.created_by,
      createdAt: r.created_at,
    })) as ReportTemplate[];
  }
};
